import { Link, Redirect, Tabs, RelativePathString, usePathname, useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { CustomTextStyles } from '@/components/CustomText';
import { useColorScheme } from '@/components/useColorScheme';
import Colors from '@/constants/Colors';
import { Image } from 'expo-image';
import { Button } from 'react95-native-rabbl';
import { createBottomTabNavigator, BottomTabBarProps, BottomTabBarButtonProps } from "expo-router/js-tabs";
import { useLinkBuilder, useTheme } from "expo-router/react-navigation";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppConstants from '@/utilities/AppConstants';
import { ExternalLink } from '@/components/ExternalLink';
import { useClientOnlyValue } from '@/components/useClientOnlyValue';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { UNKNOWN_COMMUNITY } from '@/types/community';
import DrawerButton from '@/components/DrawerButton';
import { HeaderLeft } from '@/components/HeaderLeft';

const styles = StyleSheet.create({
  image28x28: {
    width: 28,
    height: 28,
  },
  headerIcon: {
    width: 28,
    height: 28,
    marginLeft: 15,
    marginRight: 10,
  },
  tabButtonsContainerStyle: {
    flexDirection: 'row',
    gap: 5,
    justifyContent: 'center',
    padding: 5,
  },
  tabButtonContainerStyle: {
    width: 75,
  },
});

function MyTabBar({ state, descriptors, navigation }: BottomTabBarProps) {
  const { colors } = useTheme();
  //const { buildHref } = useLinkBuilder();

  return (
    <View style={styles.tabButtonsContainerStyle}>
      {state.routes.map((route, index) => {
        const { options } = descriptors[route.key];
        const label : any =
          options.tabBarLabel !== undefined
            ? options.tabBarLabel
            : options.title !== undefined
              ? options.title
              : route.name;

        const isFocused = state.index === index;

        /*const onPress = () => {
          const event = navigation.emit({
            type: 'tabPress',
            target: route.key,
            canPreventDefault: true,
          });

          if (!isFocused && !event.defaultPrevented) {
            navigation.navigate(route.name, route.params);
          }
        };

        const onLongPress = () => {
          navigation.emit({
            type: 'tabLongPress',
            target: route.key,
          });
        };*/

        if (!AppUtils.isDevelopment()) {
          if (route.name == 'test') {
            return null;
          }
        }

        return (
          <View
            style={styles.tabButtonContainerStyle}
            key={route.name}
          >
            {options.tabBarIcon && options?.tabBarIcon({ focused: isFocused, color: isFocused ? colors.primary : colors.text, size: 28})}
          </View>
        );
      })}
    </View>
  );
}

// You can explore the built-in icon families and icons on the web at https://icons.expo.fyi/
/*
function TabBarIcon(props: {
  name: React.ComponentProps<typeof FontAwesome>['name'];
  color: string;
}) {
  return <FontAwesome size={28} style={{ marginBottom: -3 }} {...props} />;
}
*/

export default function TabLayout() {
  const colorScheme = useColorScheme();
  const creating_community = useSelector((state: RootState) => state.user.creating_community);
  const userInfo = useSelector((state: RootState) => state.user.user_info);
  const router = useRouter();

  useEffect(() => {
    if (AppUtilities.DEBUGLEVEL.dev) {
      AppUtilities.info("main-public TabLayout useEffect: " + creating_community.id);
    }
    if (creating_community && creating_community.id != UNKNOWN_COMMUNITY.id) {
      if (AppUtilities.DEBUGLEVEL.dev) {
        AppUtilities.info("main-public TabLayout has creating_community");
      }
      const nextPage = AppUtils.getRabblCreateNextPageLink(creating_community, userInfo);

      // We only show the waiting approval page when the rabbl is first
      // created but otherwise if they come back to the home page, for exampe,
      // then we don't want to redirect them back to the waiting for approval page.
      // They can see the waiting for approval status on the Rabbls page.
      if (nextPage != '/' && nextPage != AppUtils.WAITING_APPROVAL_LINK) {
        if (AppUtilities.DEBUGLEVEL.dev) {
          AppUtilities.info("main-public TabLayout render detected next page: " + nextPage);
        }
        router.replace(nextPage as RelativePathString);
        //return <Redirect href={nextPage as RelativePathString} />;
      }
    }
  }, [creating_community]);

  return (
    <Tabs
      tabBar={(props) => <MyTabBar {...props} />}
      backBehavior='fullHistory'
      screenOptions={{

        // An animation is needed because we use transparent backgrounds on everything and
        // Tabs by default keeps everything mounted, so without the animation moving the tab
        // off screen, the tabs overlap.
        //animation: 'fade',
        //animation: 'shift',
        animation: 'fade',

        headerLeft: () => <HeaderLeft extraPadding={true} />,

        headerRight: () => <DrawerButton />,

        tabBarActiveTintColor: Colors(colorScheme).tint,

        // Disable the static render of the header on web
        // to prevent a hydration error in React Navigation v6.
        headerShown: useClientOnlyValue(false, true),

        headerStyle: {
          backgroundColor: 'transparent',

          /*
          ...Platform.select({
            web: {
              position: 'sticky',
              top: 0,
            },
          }),
          */
        },

        sceneStyle: {
          backgroundColor: 'transparent',
        },

        tabBarShowLabel: false,

        tabBarHideOnKeyboard: true,

        headerTitleStyle: CustomTextStyles.headerTitle,

        // Remove horizontal bottom line for header
        headerShadowVisible: false,

        tabBarStyle: {
          backgroundColor: 'transparent',
          
          //position: 'absolute',

          //paddingBottom: 5,
          //padding: 0,
          //margin: 0,

          /* Start: remove horizontal top line for tab bar */
          elevation: 0,      // For Android (removes shadow)
          borderTopWidth: 0, // For iOS
          shadowOffset: {    // For iOS (removes shadow)
            width: 0,
            height: 0,
          },
          /* End: remove horizontal top line for tab bar */
        },

        /* Tab bar blurred background */
        /*
        tabBarBackground: () => (
          <BlurView
            tint="light"
            intensity={20}
            style={[
              StyleSheet.absoluteFill,
            ]}
          />
        ),
        */
      }}>
      <Tabs.Screen
        name="index"
        options={{
          title: 'Rabbl',
          tabBarIcon: ({ focused, color }) => <Link href="/" asChild={true} className={"nontext"}><Button active={focused}><Image source={require('@/assets/images/butterflyicon.png')} style={styles.image28x28} /></Button></Link>,
        }}
      />
      <Tabs.Screen
        name="findrabbl"
        options={{
          title: 'Find a Rabbl',
          tabBarIcon: ({ focused, color }) => <Link href="/findrabbl" asChild={true} className={"nontext"}><Button active={focused}><Image source={require('@/assets/images/w98_magnifying_glass_4.png')} style={styles.image28x28} /></Button></Link>,
        }}
      />
      <Tabs.Screen
        name="rabbls"
        options={{
          title: 'Your Rabbls',
          tabBarIcon: ({ focused, color }) => <Link href="/rabbls" asChild={true} className={"nontext"}><Button active={focused}><Image source={require('@/assets/images/flowersbutterfly.png')} style={styles.image28x28} /></Button></Link>,
        }}
      />
    </Tabs>
  );
}
